/**
 * Shared formatters for v4 UI.
 * canonical: used across S0–S5 for currency display.
 */

/**
 * Format a number (or pg numeric string) as USD with exact cents.
 * Returns "—" for null / undefined / NaN.
 */
export function formatCurrency(amount: number | string | null | undefined): string {
  if (amount == null || amount === '') return '—'
  const n = typeof amount === 'number' ? amount : parseFloat(String(amount).replace(/[$,\s]/g, ''))
  if (isNaN(n)) return '—'
  return '$' + n.toLocaleString('en-US', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2
  })
}

/** Alias — exact cents, never abbreviated. Use in Soren glance + metric strip. */
export function formatMoney(amount: number | string | null | undefined): string {
  if (amount == null || amount === '') return '$0.00'
  const n = typeof amount === 'number' ? amount : parseFloat(String(amount).replace(/[$,\s]/g, ''))
  if (isNaN(n)) return '$0.00'
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(n)
}

/** Strip currency formatting; returns NaN for empty/invalid. */
export function parseMoneyInput(raw: string): number {
  const n = parseFloat(String(raw || '').replace(/[^0-9.]/g, ''))
  return Number.isFinite(n) ? n : NaN
}

/** Format for money input display on blur (no $ prefix — field context provides it). */
export function formatMoneyInputBlur(raw: string): string {
  const n = parseMoneyInput(raw)
  if (Number.isNaN(n)) return raw
  return n.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })
}

/** Props helper: onBlur handler for money text inputs. */
export function moneyInputBlurHandler(
  value: string,
  setValue: (v: string) => void,
): () => void {
  return () => setValue(formatMoneyInputBlur(value))
}
